import { Plus, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FC } from "react";

import {
  ConfirmDeleteDialog,
  CountryField,
  DateField,
  FormRow,
  FormSheet,
  FormSubheading,
  PhoneField,
  SelectField,
  TextareaField,
  TextField
} from "@/components/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { todayDateOnly } from "@/lib/dateOnly";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import type { ForeignNationalDependent } from "../../../../api/types/foreignNationalDependent";
import { IMMIGRATION_STATUS_OPTION_GROUPS } from "../../../../constants/immigrationStatusOptions";
import {
  ClientTableToolbar,
  ToolbarFilterPill
} from "../../components/table/ClientTableToolbar";
import { useClientProfile } from "../../context/ClientProfileContext";
import { useFocusScroll } from "../../hooks/useFocusScroll";
import { useRecordCrud } from "../../hooks/useRecordCrud";
import {
  RELATIONSHIP_LABEL,
  RELATIONSHIP_ORDER,
  dependentName
} from "../../lib/dependents";
import { NAV_ITEMS } from "../../navConfig";
import { DependentsTable } from "./DependentsTable";
import {
  compareDependents,
  dependentDraftSchema,
  dependentDraftToPayload,
  dependentFocusDomId,
  dependentToDraft,
  emptyDependentDraft,
  relationshipFilters,
  type DependentDraft,
  type DependentPayload,
  type RelationshipFilter
} from "./dependentsModel";

type EditorTab = "basic" | "immigration";

/** Fields on the Basic tab; a submit error there switches the sheet to it. */
const BASIC_FIELDS: ReadonlyArray<keyof DependentDraft> = [
  "relationship",
  "firstName",
  "middleName",
  "lastName",
  "birthday",
  "email",
  "phone",
  "notes"
];

const STATUS_GROUPS = IMMIGRATION_STATUS_OPTION_GROUPS.map((g) => ({
  label: g.label,
  options: g.options.map((o) => ({ value: o.value, label: o.label ?? o.value }))
}));

const RELATIONSHIP_OPTIONS = RELATIONSHIP_ORDER.map((r) => ({
  value: r,
  label: RELATIONSHIP_LABEL[r]
}));

const getId = (d: ForeignNationalDependent) => d.dependent_id;

const dependentHaystack = (d: ForeignNationalDependent): string =>
  [
    dependentName(d),
    RELATIONSHIP_LABEL[d.relationship] ?? "",
    d.country_of_birth ?? "",
    d.country_of_citizenship ?? "",
    d.current_immigration_status ?? "",
    d.email ?? "",
    d.notes ?? ""
  ]
    .join(" ")
    .toLowerCase();

/** Dependents: toolbar (search, relationship filter, add) over a table, edited in a tabbed Sheet. */
export const DependentsTab: FC = () => {
  const { clientId, url } = useClientProfile();
  const [filter, setFilter] = useState<RelationshipFilter>("ALL");
  const [search, setSearch] = useState("");
  const [pendingRemove, setPendingRemove] =
    useState<ForeignNationalDependent | null>(null);

  useEffect(() => {
    setFilter("ALL");
    setSearch("");
  }, [clientId]);

  const crud = useRecordCrud<
    ForeignNationalDependent,
    DependentDraft,
    DependentPayload
  >({
    clientId,
    noun: "Dependent",
    list: ForeignNationalsAPI.listDependents,
    create: ForeignNationalsAPI.createDependent,
    update: ForeignNationalsAPI.updateDependent,
    remove: ForeignNationalsAPI.deleteDependent,
    getId,
    toDraft: dependentToDraft,
    emptyDraft: emptyDependentDraft,
    toPayload: dependentDraftToPayload,
    schema: dependentDraftSchema,
    sort: compareDependents
  });

  const filters = useMemo(
    () => relationshipFilters(crud.records),
    [crud.records]
  );
  const visible = useMemo(() => {
    const byRelationship =
      filter === "ALL"
        ? crud.records
        : crud.records.filter((d) => d.relationship === filter);
    const q = search.trim().toLowerCase();
    return q
      ? byRelationship.filter((d) => dependentHaystack(d).includes(q))
      : byRelationship;
  }, [crud.records, filter, search]);

  // Deep link: scroll + highlight the card, and open its editor once.
  const focusId = url.state.focus.dependentId ?? null;
  const ready = !crud.isLoading && crud.records.length > 0;
  const { highlightedId } = useFocusScroll(
    focusId ? dependentFocusDomId(focusId) : null,
    ready
  );
  const openedFocusRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusId || !ready || openedFocusRef.current === focusId) return;
    const match = crud.records.find((d) => getId(d) === focusId);
    if (!match) return;
    openedFocusRef.current = focusId;
    setFilter("ALL");
    crud.openEdit(match);
    // Keyed on the record list, not `crud`, so a stable focus id opens once.
  }, [focusId, ready, crud.records]);

  const { form, editor } = crud;
  const [tab, setTab] = useState<EditorTab>("basic");
  useEffect(() => {
    if (editor.mode !== "closed") setTab("basic");
  }, [editor.mode]);

  // Errors on a hidden tab would go unseen; jump to the tab that has them.
  const { errors, submitCount } = form.formState;
  const basicHasError = BASIC_FIELDS.some((k) => Boolean(errors[k]));
  const immigrationHasError =
    Object.keys(errors).length > 0 &&
    Object.keys(errors).some(
      (k) => !BASIC_FIELDS.includes(k as keyof DependentDraft)
    );
  useEffect(() => {
    if (submitCount === 0) return;
    if (basicHasError) setTab("basic");
    else if (immigrationHasError) setTab("immigration");
  }, [submitCount, basicHasError, immigrationHasError]);

  const isEditing = editor.mode === "edit";
  const editingRecord =
    editor.mode === "edit"
      ? crud.records.find((d) => getId(d) === editor.id)
      : undefined;
  const editingName = editingRecord ? dependentName(editingRecord) : "";

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <ClientTableToolbar
        title={NAV_ITEMS.dependents.label}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Search dependents...",
          ariaLabel: "Search dependents"
        }}
        filters={
          filters.length > 0 ? (
            <ToolbarFilterPill<RelationshipFilter>
              appearance="ghost"
              icon="FeatherUsers"
              label="Relationship"
              ariaLabel="Filter by relationship"
              value={filter}
              options={filters.map((f) => ({
                value: f.value,
                label: `${RELATIONSHIP_LABEL[f.value]} · ${f.count}`
              }))}
              valueLabel={filter === "ALL" ? "All" : RELATIONSHIP_LABEL[filter]}
              allOption={{
                value: "ALL",
                label: `All · ${crud.records.length}`
              }}
              onChange={setFilter}
            />
          ) : null
        }
        onRefresh={() => crud.refresh().catch(() => undefined)}
        isRefreshing={crud.isLoading}
        primaryAction={{
          label: "Add Dependent",
          onClick: () => crud.openCreate()
        }}
      />
      <DependentsTable
        dependents={visible}
        isLoading={crud.isLoading}
        error={crud.error}
        onRetry={() => crud.refresh().catch(() => undefined)}
        highlightedDomId={highlightedId}
        onEdit={(d) => crud.openEdit(d)}
        onRemove={setPendingRemove}
        empty={
          crud.records.length === 0
            ? {
                title: "No Dependents Yet",
                icon: Users,
                message:
                  "Add spouses, children and other family members whose immigration status travels with this client.",
                cta: "Add Dependent",
                ctaIcon: Plus,
                onCta: () => crud.openCreate()
              }
            : {
                title: "No Dependents Match This Filter",
                icon: Users,
                cta: "Show All",
                onCta: () => {
                  setFilter("ALL");
                  setSearch("");
                }
              }
        }
      />

      <FormSheet
        form={form}
        open={crud.editor.mode !== "closed"}
        onOpenChange={(open) => {
          if (!open) crud.close();
        }}
        title={isEditing ? editingName || "Edit dependent" : "Add dependent"}
        description={
          isEditing
            ? "Update this dependent's details."
            : "A family member tied to this client."
        }
        onSubmit={crud.submit}
        isEditing={isEditing}
        isSubmitting={crud.isSaving}
        isDeleting={crud.isDeleting}
        onDelete={() => {
          crud.deleteCurrent().catch(() => undefined);
        }}
        deleteTitle="Remove this dependent?"
        size="lg"
      >
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as EditorTab)}
          className="w-full"
        >
          <TabsList className="w-full">
            <TabsTrigger
              value="basic"
              className="flex-1"
              data-invalid={basicHasError || undefined}
            >
              Basic
              {basicHasError ? (
                <span
                  className="size-1.5 rounded-full bg-destructive"
                  aria-label="Has errors"
                />
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="immigration" className="flex-1">
              Immigration
              {immigrationHasError ? (
                <span
                  className="size-1.5 rounded-full bg-destructive"
                  aria-label="Has errors"
                />
              ) : null}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="flex flex-col gap-4 pt-4">
            <FormRow>
              <TextField<DependentDraft>
                name="firstName"
                label="First Name"
                required
                autoComplete="off"
              />
              <TextField<DependentDraft>
                name="middleName"
                label="Middle Name"
              />
              <TextField<DependentDraft>
                name="lastName"
                label="Last Name"
                required
              />
            </FormRow>
            <FormRow>
              <SelectField<DependentDraft>
                name="relationship"
                label="Relationship"
                required
                placeholder="Select..."
                options={RELATIONSHIP_OPTIONS}
              />
              <DateField<DependentDraft>
                name="birthday"
                label="Date of Birth"
                max={todayDateOnly()}
              />
            </FormRow>
            <FormRow>
              <TextField<DependentDraft>
                name="email"
                label="Email Address"
                type="email"
              />
              <PhoneField<DependentDraft> name="phone" label="Phone Number" />
            </FormRow>
            <TextareaField<DependentDraft>
              name="notes"
              label="Notes"
              placeholder="Add any additional notes about this dependent..."
            />
          </TabsContent>

          <TabsContent value="immigration" className="flex flex-col gap-4 pt-4">
            <FormRow>
              <CountryField<DependentDraft>
                name="countryOfBirth"
                label="Country of Birth"
              />
              <CountryField<DependentDraft>
                name="countryOfCitizenship"
                label="Country of Citizenship"
              />
            </FormRow>
            <FormRow>
              <SelectField<DependentDraft>
                name="currentImmigrationStatus"
                label="Current Immigration Status"
                groups={STATUS_GROUPS}
                clearable
              />
              <DateField<DependentDraft>
                name="immigrationStatusExpiration"
                label="Status Expiration"
              />
            </FormRow>
            <FormRow>
              <DateField<DependentDraft>
                name="priorityDate"
                label="Priority Date"
              />
              <TextField<DependentDraft>
                name="preferenceCategory"
                label="Preference Category"
              />
            </FormRow>
            <FormSubheading>Expiration Dates</FormSubheading>
            <FormRow>
              <DateField<DependentDraft> name="eadExpiration" label="EAD" />
              <DateField<DependentDraft>
                name="advanceParoleExpiration"
                label="Advance Parole"
              />
            </FormRow>
            <FormRow>
              <DateField<DependentDraft>
                name="passportExpiration"
                label="Passport"
              />
              <DateField<DependentDraft> name="visaExpiration" label="Visa" />
            </FormRow>
          </TabsContent>
        </Tabs>
      </FormSheet>

      <ConfirmDeleteDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        title="Remove this dependent?"
        description={
          pendingRemove
            ? `${
                dependentName(pendingRemove) || "This dependent"
              } will be removed from the client's profile. This action cannot be undone.`
            : undefined
        }
        confirmLabel="Remove"
        isPending={crud.isDeleting}
        onConfirm={() => {
          if (!pendingRemove) return;
          const id = getId(pendingRemove);
          crud
            .deleteRecord(id)
            .then(() => setPendingRemove(null))
            .catch(() => undefined);
        }}
      />
    </div>
  );
};
